// Earth accuracy badge (ERT-3, E-41). PURE (ENG-1). The world's honest fidelity
// claim derives from `TILE.ACCURACY_CLAIM_M[demSource] ≤ scaleM` (via
// `claimsBlockAccuracy`): a fine-enough DEM at the world's scale earns a ±1-block
// claim; otherwise the world is badged "approximate relief" — never a false ±1
// (the ATL-2 SIMULATED-badge pattern). A scale that forced an elevation clamp
// (E-41) is called out too.
import { claimsBlockAccuracy, DEM_SOURCE_IDS, type DemSourceId } from './tiles';
import { earthRangeClamped } from './range';

/** Human labels for the DEM sources (credits/attribution, ELV-1 pattern). */
const SOURCE_LABEL: Record<string, string> = {
  '3dep-1m': 'USGS 3DEP 1 m',
  'srtm-30m': 'NASA SRTM 30 m',
};

export interface EarthBadge {
  /** True when the world justifies the ±1-block accuracy claim (ERT-3). */
  claim: boolean;
  /** True when the scale forced an elevation clamp (E-41). */
  clamped: boolean;
  /** Short badge text for the HUD / world-select. */
  text: string;
}

/** The accuracy badge for an Earth world at `demSource` + `scaleM` (ERT-3 / E-41). */
export function earthBadge(demSource: string, scaleM: number): EarthBadge {
  const src = (DEM_SOURCE_IDS as readonly string[]).includes(demSource) ? (demSource as DemSourceId) : undefined;
  const label = SOURCE_LABEL[demSource] ?? demSource;
  const claim = src ? claimsBlockAccuracy(src, scaleM) : false;
  const clamped = earthRangeClamped(scaleM);
  const base = claim ? `±1 block · ${label}` : `approximate relief · ${label}`;
  return { claim, clamped, text: clamped ? `${base} · elevation clamped` : base };
}
